import classnames from 'classnames';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { LightsAndSirens } from '@icons';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { LocationMarker } from '@lp/features/RoutePlanner/components/RouteViewList/LocationMarker';
import { getMarkerName } from '@lp/features/RoutePlanner/components/RouteViewList/SegmentLegInformation';
import { AviaLegTypes, GroundLegTypes, LegLocationTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { isGroundLegType, REPOSITION_SEGMENT_TYPES } from '@/utils/route';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

type LegInformationHeaderProps = {
  leg: AviaLegTypes | GroundLegTypes;
  isOpen: boolean;
  onOpenChange: () => void;
};

export const LegInformationHeader = ({ leg, isOpen, onOpenChange }: LegInformationHeaderProps) => {
  const { isLightsSirensRequired } = useCaseDetails();
  const { getLocationById } = useRouteLocations();
  const { extendLocation } = useCountriesData();
  const startLocationData = getLocationById(leg.departureLocation.locationId);
  const endLocationData = getLocationById(leg.arrivalLocation.locationId);

  const startLocationVariant =
    leg.departureLocation.locationType === LegLocationTypeEnum.airport ? 'outlined' : 'filled';
  const endLocationVariant = leg.arrivalLocation.locationType === LegLocationTypeEnum.airport ? 'outlined' : 'filled';

  const toTxCenter = leg.arrivalLocation.locationType === LegLocationTypeEnum.txcenter;
  const isGround = isGroundLegType(leg);
  const includesDevice = leg.resources.find((r) => r.resourceType === CaseResourceType.OCSConsole);
  const isReposition = REPOSITION_SEGMENT_TYPES.includes(leg.segmentType);

  return (
    <div className="flex flex-row gap-6 justify-between items-center">
      <div className="flex flex-row gap-2 items-center">
        <div className="flex gap-2 items-center">
          <LocationMarker size="sm" variant={startLocationVariant} type="start" segmentType={leg.segmentType}>
            {getMarkerName(startLocationData && extendLocation(startLocationData))}
          </LocationMarker>
          <span className="text-l-m text-base-5">-</span>
          <LocationMarker size="sm" variant={endLocationVariant} type="end" segmentType={leg.segmentType}>
            {getMarkerName(endLocationData && extendLocation(endLocationData))}
          </LocationMarker>
        </div>
        {isReposition && (
          <span className="text-m-r text-base-10">
            {leg.segmentType === SegmentTypeEnum.reposition ? 'Reposition' : 'Return to base'}
          </span>
        )}
        {isGround && isLightsSirensRequired && includesDevice && toTxCenter && (
          <span className="px-1 py-0.5 text-xs-m text-info-40 flex justify-center items-center">
            <LightsAndSirens height={16} width={16} />
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={onOpenChange}
        className={classnames('bg-base-50 size-7.5 flex rounded-full items-center justify-center cursor-pointer', {
          'rotate-90': isOpen,
        })}
      >
        <MdOutlineKeyboardArrowRight size={20} />
      </button>
    </div>
  );
};
